import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cx from 'classnames';
import { FlagsProvider } from 'react-feature-flags';
import styles from './App.module.scss';
import WishListsStore from './stores/WishListsStore';
import { EmailMeModal } from './components/EmailMeModal/EmailMeModal';
import EmailModalStore from './stores/EmailModalStore/EmailModalStore';
import { getFlagsFromCookies } from './helpers/featureFlags/featureFlags';
import { FEATURE_FLAGS } from './helpers/featureFlags/constants';
import Container from './container/Container';
import { createRoot } from 'react-dom/client';
import AddToWishListIcon from './components/AddToWishListIcon/AddToWishListIcon';
import AddToWishListIconButton from './components/AddToWishListIconButton/AddToWishListIconButton';
import { WishListModal } from './components/WishListModal/WishListModal';

interface AppProps {
    wishListStore?: WishListsStore;
}

const emailModalStore = new EmailModalStore();

export const App: React.FC<AppProps> = observer(() => {
    const [store] = useState(new WishListsStore(emailModalStore));
    const {
        wishLists,
        products,
        mode,
        isCallingAPI,
        productExistsInWishLists,
        hasError,
        errorMessage,
        hasMoreThanOneWishlist,
        shouldWishListsBeExpanded,
        currentProductId,
        currentProductSkuId,
        currentProductMetadata,
        mapProductWishListForCurrentSku,
        getWishlists,
        handleWishListOnClick,
        handleWishListIconClick,
        checkIfWishListInURL,
        setupEventListeners,
        isProductInWishList,
        isSkuInWishList,
        resetProductsList,
    } = store;

    const flags: FeatureFlag[] = getFlagsFromCookies([FEATURE_FLAGS.NURSERY_PROTOTYPE, FEATURE_FLAGS.COLLAPSED_BUTTON]);

    useEffect(() => {
        const esiContainer = document.getElementById('add-to-wish-list-esi');

        // could we have a second version?
        // recsEsiContainer = document.getElementById('add-to-wish-list-esi-recs');

        const esiMode = esiContainer?.getAttribute('data-mode');
        store.setMode(esiMode ?? 'button');

        if (esiContainer && esiMode === 'icon-injected') {
            const dataProducts = esiContainer.getAttribute('data-products');
            if (dataProducts) {
                // Set a delay to work around mobile specific rendering of plp
                setTimeout(() => {
                    injectWishListIcons(JSON.parse(dataProducts));
                }, 1000);
            }

            new MutationObserver(() => {
                const dataProducts = esiContainer.getAttribute('data-products');
                if (dataProducts) {
                    injectWishListIcons(JSON.parse(dataProducts));
                }
            }).observe(esiContainer, {
                attributes: true,
                attributeFilter: ['data-products'],
            });
        }

        if (esiMode === 'icon-placeholder-pdp') {
            findPlaceholdersAndInjectIcons();

            // need to add extra event listener to listen for selection of default size variant due to existing analytics bug on PDP
            window.addEventListener('ADD_TO_BASKET_ESI:VARIANT_CHANGE_FOR_WISHLIST', findPlaceholdersAndInjectIcons);
        }

        if (esiMode === 'icon-placeholder-wishlist-recs') {
            findPlaceholdersAndInjectIcons();

            // need to add event listener for listing ui products change to update the filled icons in recs in wishlist
            window.addEventListener('LISTING_UI:PRODUCTS_CHANGE_FOR_WISHLIST', async () => {
                await getWishlists();
                findPlaceholdersAndInjectIcons();
            });
        }

        if (
            esiMode === 'icon-placeholder' ||
            esiMode === 'icon-placeholder-wishlist-recs' ||
            esiMode === 'icon-placeholder-pdp' ||
            esiMode === 'icon-placeholder-basket'
        ) {
            findPlaceholdersAndInjectIcons();

            // ToDo - Variant changes/selections need to trigger an update for PDP?
            // Listening to this event guarantees the PLP grid is updated with new set of product cards and their new positions
            document.body.addEventListener('dataStateChange', findPlaceholdersAndInjectIcons);
        }

        setupEventListeners();

        if (navigator.userAgent !== 'ReactSnap') {
            // attempt network call only when not pre-rendering
            getWishlists().then(() => {
                checkIfWishListInURL();
                store.checkIfProductInURL();
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!mode) return;

        mapProductWishListForCurrentSku();

        if (products.length) {
            products.forEach(({ root, ...product }) => {
                let inWishList: boolean;
                if (store.mode === 'icon-placeholder-basket') {
                    inWishList = isSkuInWishList(product.skuId);
                } else if (store.mode === 'icon-placeholder-pdp') {
                    inWishList = isSkuInWishList(product.skuId) && !!product.variantId;
                } else {
                    inWishList = isProductInWishList(product.id, product.skuId);
                }

                if (store.mode === 'icon-placeholder-basket') {
                    root?.render(
                        <AddToWishListIconButton
                            product={product}
                            inWishList={inWishList}
                            isSubmitting={isCallingAPI && product.id === store.currentProductId}
                            handleOnClick={handleWishListIconClick}
                            mode={store.mode}
                        />,
                    );
                } else {
                    root?.render(
                        <AddToWishListIcon
                            product={product}
                            inWishList={inWishList}
                            isSubmitting={isCallingAPI && product.id === store.currentProductId}
                            handleOnClick={handleWishListIconClick}
                            mode={store.mode}
                        />,
                    );
                }

                if (store.interactedProductId === product.id) {
                    handleWishListIconClick(product, inWishList);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, wishLists, currentProductMetadata, currentProductSkuId, currentProductId, isCallingAPI]);

    const appClassNames = cx(styles.wishListsContainer, {
        [styles.expanded]: shouldWishListsBeExpanded,
    });

    // Attempt using placeholder on product card, this is here for when PLP adds placeholders
    function findPlaceholdersAndInjectIcons(): void {
        resetProductsList(); // Reset the list every time before making a new one

        // Timeout needed to wait for the DOM changes to get completed so that we can access all the placeholder elements,
        // including any new ones added by scrolling down the page.
        setTimeout(() => {
            const placeHolderElements = document.querySelectorAll('[data-wishlist-placeholder]');

            placeHolderElements.forEach((placeHolderElement) => {
                const id = placeHolderElement.getAttribute('data-product-id');
                const skuId = placeHolderElement.getAttribute('data-product-skuid');
                const variantId = placeHolderElement.getAttribute('data-product-variantid') || undefined;
                const useOutOfStockButton =
                    placeHolderElement.getAttribute('data-product-oos-button') === 'true' || false;
                const isMultiSku = placeHolderElement.getAttribute('data-product-multi-sku') === 'true';
                const url = placeHolderElement.getAttribute('data-product-url') ?? '';
                if (id && skuId) {
                    placeHolderElement.setAttribute('data-injected', '');
                    store.addIconProduct({
                        id,
                        url,
                        skuId,
                        variantId,
                        isMultiSku,
                        useOutOfStockButton,
                        root: createRoot(placeHolderElement),
                    });
                }
            });
        }, 500);
    }

    // TODO: Remove once PLP has added placeholders, this injects the icon into the c-cta-buttons container position
    function injectWishListIcons(products: WishListIconProduct[]): void {
        products.forEach((product) => {
            const productElement = document.querySelector(`[data-product-id="${product.id}"]:not([data-injected])`);
            if (productElement) {
                productElement.setAttribute('data-injected', '');

                let iconContainer = productElement.querySelector('[class^="product-card_c-cta-buttons__right"]');
                if (!iconContainer) {
                    iconContainer = document.createElement('div');
                    iconContainer.className = 'product-card_c-cta-buttons__right__WishList';
                    productElement.querySelector('[data-test="product-image-container"]')?.append(iconContainer);
                }

                const iconWrapper = document.createElement('div');
                iconContainer?.prepend(iconWrapper);
                store.addIconProduct({
                    ...product,
                    root: createRoot(iconWrapper),
                });
            }
        });
    }

    return (
        <>
            {mode === 'button' && (
                <FlagsProvider value={flags}>
                    <div
                        className={appClassNames}
                        data-testid="add-to-wish-list-app"
                        data-sku={currentProductSkuId}
                        data-product-metadata={currentProductMetadata}
                        data-product-details={JSON.stringify({
                            productId: currentProductId,
                        })}
                    >
                        <Container
                            handleWishListOnClick={handleWishListOnClick}
                            productExistsInWishLists={productExistsInWishLists}
                            shouldWishListsBeExpanded={shouldWishListsBeExpanded}
                            wishLists={wishLists}
                            isCallingAPI={isCallingAPI}
                            hasError={hasError}
                            errorMessage={errorMessage}
                            hasMoreThanOneWishlist={hasMoreThanOneWishlist}
                        />
                    </div>

                    <EmailMeModal emailModalStore={emailModalStore} />
                </FlagsProvider>
            )}

            {mode?.startsWith('icon-') && <WishListModal wishListStore={store} />}
        </>
    );
});
